import type { RefObject } from 'react';
import { merge } from 'lodash-es';
import React from 'react';

import type { MaxWidthTypes } from '../../utilities/shared/MaxWidth';
import type { MinWidthTypes } from '../../utilities/shared/MinWidth';
import type { WidthTypes } from '../../utilities/shared/Width';
import type {
  ShellPropContentMode,
  ShellPropLayoutMode,
  ShellPropSize,
  ShellPropType,
  ShellPropWidthMode,
} from './utils';
import { styled } from '../../stitches.config';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import {
  ShellContentModeProvider,
  ShellLayoutModeProvider,
  ShellMaxWidthProvider,
  ShellMinWidthProvider,
  ShellSizeProvider,
  ShellTypeProvider,
  ShellWidthModeProvider,
  ShellWidthProvider,
} from './utils';

export const ShellContainer = styled('div', {
  display: 'grid',
  gridTemplateAreas: '"shell-toolbar" "shell-header" "shell-content" "shell-footer"',
  width: '100%',
  height: '100%',
  overflow: 'hidden',

  '& form': {
    // This allows for the use of a `<form>` inside of any `Shell` components
    // without affecting the flexbox of the children.
    display: 'contents',
  },

  variants: {
    contentMode: {
      fit: {
        gridTemplateRows:
          'fit-content(100%) fit-content(100%) minmax(0, min-content) fit-content(100%)',
      },
      stretch: {
        gridTemplateRows: 'fit-content(100%) fit-content(100%) 1fr fit-content(100%)',
      },
    },
    widthMode: {
      centered: {},
      full: {},
    },
  },
});
ShellContainer.displayName = 'ShellContainer';

export type ShellProps = {
  children: React.ReactNode;
  contentMode?: ShellPropContentMode;
  layoutMode?: ShellPropLayoutMode;
  size?: ShellPropSize;
  width?: WidthTypes;
  maxWidth?: MaxWidthTypes;
  minWidth?: MinWidthTypes;
  widthMode?: ShellPropWidthMode;
  type?: ShellPropType;
  ref?: RefObject<HTMLDivElement | null>;
};

export function Shell({
  children,
  contentMode = 'stretch',
  layoutMode = 'compact',
  size = 'medium',
  width,
  maxWidth,
  minWidth = 0,
  widthMode = 'full',
  type = 'pane',
  ref,
  ...remaining
}: ShellProps) {
  return (
    <ShellSizeProvider value={size}>
      <ShellLayoutModeProvider value={layoutMode}>
        <ShellContentModeProvider value={contentMode}>
          <ShellWidthModeProvider value={widthMode}>
            <ShellWidthProvider value={width}>
              <ShellMaxWidthProvider value={maxWidth}>
                <ShellMinWidthProvider value={minWidth}>
                  <ShellTypeProvider value={type}>
                    <ShellContainer
                      ref={ref}
                      contentMode={contentMode}
                      widthMode={widthMode}
                      css={merge(
                        widthMode === 'full' ? widthCSS(width) : undefined,
                        widthMode === 'full' ? maxWidthCSS(maxWidth) : undefined,
                        widthMode === 'full' ? minWidthCSS(minWidth) : undefined,
                        // Named container for `@container pane|drawer|dialog` rules in shell chrome (header/footer).
                        { containerType: 'inline-size', containerName: type },
                      )}
                      {...remaining}
                    >
                      {children}
                    </ShellContainer>
                  </ShellTypeProvider>
                </ShellMinWidthProvider>
              </ShellMaxWidthProvider>
            </ShellWidthProvider>
          </ShellWidthModeProvider>
        </ShellContentModeProvider>
      </ShellLayoutModeProvider>
    </ShellSizeProvider>
  );
}
